import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'
import { buildSlotSequencePairs } from '../slotPayloads'

import type { SlotRow } from '../slotPayloads'
import type { StepResult } from '../types'
import type { RouteId, ServiceDate } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

const BATCH_SIZE = 200

export interface AssignDailySlotStepArgs {
  readonly routeId: RouteId
  readonly serviceDate: ServiceDate
  readonly rows: ReadonlyArray<SlotRow>
  readonly startSequence?: number
}

export type AssignDailySlotStepValue = {
  readonly stopCount: number
  readonly batchCount: number
}

export type AssignDailySlotStepResult = StepResult<'ASSIGN_DAILY', AssignDailySlotStepValue>

export const makeAssignDailySlotStep = (
  args: AssignDailySlotStepArgs,
  deps: Deps
): PipelineStep<'ASSIGN_DAILY', AssignDailySlotStepResult> => {
  const stepId = `assign-daily-slot:${args.routeId}:${args.serviceDate}`
  return {
    id: stepId,
    phase: 'ASSIGN_DAILY',
    label: 'Failed to assign stops to daily schedule',
    applyOrder: 100,
    run: async ctx => {
      if (!deps.assignDailySlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'assignDailySlot dep not provided — the v3-slot strategy requires it' })
      }
      const { pairs, droppedDuplicates, droppedMissing } = buildSlotSequencePairs(args.rows, args.startSequence ?? 1)
      if (droppedDuplicates.length > 0 || droppedMissing > 0) {
        ctx.logger.warn(`${stepId}: dropped ${droppedDuplicates.length} duplicate and ${droppedMissing} slot-less rows`)
      }
      if (pairs.length === 0) {
        return ok({ phase: 'ASSIGN_DAILY', stepId, value: { stopCount: 0, batchCount: 0 } })
      }

      // Each batch lands at the head as a block, so the last one has to go first: sent in order, batch
      // two would sit above batch one and a selection past BATCH_SIZE would commit reversed.
      const batches = chunk(pairs, BATCH_SIZE).reverse()
      let stopCount = 0
      try {
        // eslint-disable-next-line no-restricted-syntax -- batches apply in order; an array method would fan them out
        for (const batch of batches) {
          // eslint-disable-next-line no-await-in-loop -- sequential by design, see above
          const response = await deps
            .assignDailySlot({
              routeId: args.routeId,
              data: { serviceDate: args.serviceDate, stops: [...batch] },
              skipInvalidate: true,
            })
            .unwrap()
          stopCount += response?.stopCount ?? 0
          ctx.recordCommit()
        }
        return ok({ phase: 'ASSIGN_DAILY', stepId, value: { stopCount, batchCount: batches.length } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'ASSIGN_DAILY', status, message })
      }
    },
  }
}
